// src/features/radar/BriefMemo.jsx
//
// RADAR-BRIEF-STACK: Right executive memo pane for the Decision Brief Stack.
//
// Props:
//   item        — radar feed item
//   impact      — seeded integer (10–99)
//   isEditor    — boolean; gates write actions
//   onTriage    — (itemKey, patch) => void
//   triageError — string | null
//   onNavigate  — optional (url) => void

import React, { useState } from "react";
import { T } from "../../app/tokens.js";
import { TYPE_META } from "../pulse/LeadershipRadar.jsx";
import {
  fmtDue,
  fmtDueAbsolute,
  dueColor,
  getSubjectDisplayName,
  whySurfaced,
} from "./radarUtils.js";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function deriveConfidence(impact) {
  if (impact >= 70) return "High";
  if (impact >= 40) return "Medium";
  return "Low";
}

const CONFIDENCE_STYLE = {
  High:   { color: T.radarAction,  bg: T.radarActionBg },
  Medium: { color: T.radarExpiry,  bg: T.radarExpiryBg },
  Low:    { color: T.mid,          bg: T.lightGray     },
};

function ActionButton({ label, onClick, primary, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        background:   primary ? T.blue : T.white,
        color:        primary ? T.white : disabled ? T.light : T.dark,
        border:       `1px solid ${primary ? T.blue : T.border}`,
        borderRadius: T.radiusSm,
        padding:      "6px 12px",
        fontSize:     T.fsSm,
        fontWeight:   T.fwSemibold,
        cursor:       disabled ? "not-allowed" : "pointer",
        whiteSpace:   "nowrap",
        opacity:      disabled ? 0.5 : 1,
        transition:   "opacity 0.15s",
      }}
    >
      {label}
    </button>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function EmptyMemo() {
  return (
    <div style={{
      display:        "flex",
      alignItems:     "center",
      justifyContent: "center",
      height:         "100%",
      color:          T.light,
      fontSize:       T.fsSm,
      fontStyle:      "italic",
      padding:        24,
      textAlign:      "center",
    }}>
      Select a brief to read the executive memo
    </div>
  );
}

// P-61b: the office/lease card is shared across Review, Decision, and Action
// drawer modes. Feed rows without enrichment still get a useful identity card;
// only the image is allowed to fall back to the gradient placeholder.
function SubjectCard({ item, subject, onNavigate }) {
  if (!subject) return null;

  const subjectName = String(subject.name ?? "").trim() || getSubjectDisplayName(item);
  const identity = [
    subject.id ?? item.location_id ?? item.subject_id,
    subject.stage,
    subject.region,
  ].filter(Boolean).join(" · ");
  const stats = [
    subject.seats != null ? `${subject.seats} seats` : null,
    subject.assigned != null ? `${subject.assigned} assigned` : null,
    subject.leaseSummary || null,
  ].filter(Boolean);

  const openSubject = () => {
    if (!onNavigate || !item.navUrl) return;
    onNavigate(item.navUrl);
  };

  return (
    <div
      data-testid="brief-memo-subject-card"
      onClick={openSubject}
      style={{
        display: "flex",
        alignItems: "stretch",
        border: `1px solid ${T.border}`,
        borderRadius: T.radiusCard,
        overflow: "hidden",
        cursor: onNavigate && item.navUrl ? "pointer" : "default",
      }}
    >
      {subject.imageUrl ? (
        <img
          data-testid="brief-memo-subject-image"
          src={subject.imageUrl}
          alt={subjectName}
          style={{
            flex: "0 0 66px",
            width: 66,
            objectFit: "cover",
            display: "block",
            alignSelf: "stretch",
          }}
        />
      ) : (
        <div
          data-testid="brief-memo-subject-placeholder"
          aria-label="Subject image unavailable"
          style={{
            flex: "0 0 66px",
            background: `linear-gradient(135deg, ${T.border}, ${T.borderCompact})`,
          }}
        />
      )}
      <div style={{ padding: "9px 11px", minWidth: 0 }}>
        <div
          data-testid="brief-memo-subject-name"
          style={{ fontWeight: T.fwBold, fontSize: T.fsBase, lineHeight: 1.3 }}
        >
          {subjectName}
        </div>
        {identity && (
          <div
            data-testid="brief-memo-subject-identity"
            style={{ fontSize: T.fsXs5, color: T.mid, marginTop: 2 }}
          >
            {identity}
          </div>
        )}
        {stats.length > 0 && (
          <div
            data-testid="brief-memo-subject-stats"
            style={{ fontSize: T.fsXs5, color: T.mid, marginTop: 5 }}
          >
            {stats.join(" · ")}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── BriefMemo ────────────────────────────────────────────────────────────────

export function BriefMemo({ item, impact, isEditor, onTriage, triageError, onNavigate }) {
  // assignMode / assignInput retained in state shape for future P-40 Lifecycle task
  // (assign owner is not wired to an API route yet — button is disabled)
  const [assignMode,  setAssignMode]  = useState(false);
  const [assignInput, setAssignInput] = useState("");

  if (!item) return <EmptyMemo />;

  const meta       = TYPE_META[item.type] ?? TYPE_META.signal;
  const confidence = deriveConfidence(impact ?? 50);
  const confStyle  = CONFIDENCE_STYLE[confidence];
  const absDate    = fmtDueAbsolute(item.dueDate);
  const relDate    = fmtDue(item.dueDate);
  const dueFg      = dueColor(item.dueDate);
  const subjectName = getSubjectDisplayName(item);
  const hasSubjectIdentity = Boolean(
    item.subject
      || item.location_id
      || item.subject_id
      || item.locationName,
  );
  const subject = hasSubjectIdentity
    ? (item.subject ?? {
        name: subjectName,
        id: item.location_id ?? item.subject_id ?? null,
        stage: null,
        region: null,
        seats: null,
        assigned: null,
        leaseSummary: null,
        imageUrl: null,
      })
    : null;
  const why        = whySurfaced(item, subjectName);

  // Location/origin sub-line: "Chicago · CHI-01 · Ripple AI" or just location
  const originValue = item.source;
  const origin = typeof originValue === "string" && originValue.trim()
    ? originValue
    : "Ripple AI";
  const subLine = item.locationName
    ? `${item.locationName} · ${origin}`
    : origin;

  // Only resolve is supported via the focus-items route.
  // Assign / snooze are deferred to the P-40 Lifecycle task.
  const handleResolve = () => onTriage(item.itemKey, { resolved: true });
  const handleAssignConfirm = () => {
    // No-op until the assign owner API route is wired (P-40 Lifecycle task)
    setAssignMode(false);
    setAssignInput("");
  };

  return (
    <div
      data-testid="brief-memo"
      style={{
        display:       "flex",
        flexDirection: "column",
        height:        "100%",
        overflowY:     "auto",
      }}
    >
      {/* ── Header ── */}
      <div style={{
        padding:      "16px 20px 14px",
        borderBottom: `1px solid ${T.border}`,
        flexShrink:   0,
      }}>
        {/* Kicker + confidence badge */}
        <div style={{
          display:      "flex",
          alignItems:   "center",
          gap:          8,
          marginBottom: 8,
        }}>
          <span style={{
            fontSize:      T.fsXxs,
            fontWeight:    T.fwBold,
            color:         meta.fg,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}>
            Leadership brief · {meta.label}
          </span>
          <span style={{
            background:   confStyle.bg,
            color:        confStyle.color,
            border:       `1px solid ${confStyle.color}33`,
            borderRadius: T.radiusFull,
            padding:      "1px 8px",
            fontSize:     T.fsXxs,
            fontWeight:   T.fwBold,
            letterSpacing: "0.03em",
          }}>
            {confidence}
          </span>
        </div>

        {/* Headline */}
        <div
          data-testid="brief-memo-headline"
          style={{
            fontSize:     T.fsLg5,
            fontWeight:   T.fwBold,
            color:        T.dark,
            lineHeight:   1.3,
            marginBottom: 4,
          }}
        >
          {item.title}
        </div>

        {/* Location / origin sub-line */}
        <div style={{ fontSize: T.fsSm, color: T.mid }}>
          {subLine}
        </div>
        <div style={{ marginTop: 12 }}>
          <SubjectCard item={item} subject={subject} onNavigate={onNavigate} />
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{
        display:       "flex",
        flexDirection: "column",
        gap:           10,
        padding:       "14px 20px",
        flex:          1,
      }}>
        {/* Why it matters (neutral) */}
          <div
            data-testid="brief-memo-why"
            style={{
          background:   T.lightGray,
          border:       `1px solid ${T.border}`,
          borderRadius: T.radiusCard,
          padding:      "10px 14px",
            }}
          >
          <div style={{
            fontSize:      T.fsXxs,
            fontWeight:    T.fwBold,
            color:         T.mid,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            marginBottom:  5,
          }}>
            Why this is here
          </div>
          <div style={{ fontSize: T.fsSm, color: T.dark, lineHeight: 1.5 }}>
            {why}
          </div>
        </div>

        {/* Recommended action (green) */}
        {item.recommendedMove && (
          <div style={{
            background:   T.radarActionBg,
            border:       `1px solid ${T.radarAction}44`,
            borderRadius: T.radiusCard,
            padding:      "10px 14px",
          }}>
            <div style={{
              fontSize:      T.fsXxs,
              fontWeight:    T.fwBold,
              color:         T.radarAction,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              marginBottom:  5,
            }}>
              Recommended action
            </div>
            <div style={{ fontSize: T.fsSm, color: T.dark, lineHeight: 1.5 }}>
              {item.recommendedMove}
            </div>
          </div>
        )}

        {/* If delayed (amber) */}
        {item.ifDelayed && (
          <div style={{
            background:   T.radarExpiryBg,
            border:       `1px solid ${T.radarExpiry}44`,
            borderRadius: T.radiusCard,
            padding:      "10px 14px",
          }}>
            <div style={{
              fontSize:      T.fsXxs,
              fontWeight:    T.fwBold,
              color:         T.radarExpiry,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              marginBottom:  5,
            }}>
              If delayed
            </div>
            <div style={{ fontSize: T.fsSm, color: T.dark, lineHeight: 1.5 }}>
              {item.ifDelayed}
            </div>
          </div>
        )}

        {/* 2×2 KV grid */}
        <div style={{
          display:             "grid",
          gridTemplateColumns: "1fr 1fr",
          gap:                 1,
          background:          T.border,
          border:              `1px solid ${T.border}`,
          borderRadius:        T.radiusCard,
          overflow:            "hidden",
        }}>
          {[
            {
              label: "Suggested owner",
              value: item.owner ?? "—",
              color: item.owner ? T.dark : T.light,
            },
            {
              label: "Due date",
              value: absDate ? `${absDate} (${relDate})` : "—",
              color: absDate ? dueFg : T.light,
            },
            {
              label: "Source",
              value: item.source ?? "Ripple AI",
              color: T.dark,
            },
            {
              label: "Recommended next step",
              value: item.recommendedMove
                ? item.recommendedMove.split(".")[0] + "."
                : "Review and assign owner",
              color: T.dark,
            },
          ].map(({ label, value, color }) => (
            <div
              key={label}
              style={{
                background: T.white,
                padding:    "9px 12px",
              }}
            >
              <div style={{
                fontSize:      T.fsXxs,
                fontWeight:    T.fwBold,
                color:         T.light,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                marginBottom:  3,
              }}>
                {label}
              </div>
              <div style={{
                fontSize:   T.fsSm,
                fontWeight: T.fwSemibold,
                color,
                lineHeight: 1.3,
              }}>
                {value}
              </div>
            </div>
          ))}
        </div>

        {/* Triage error */}
        {triageError && (
          <div
            data-testid="brief-memo-triage-error"
            style={{ fontSize: T.fsXs, color: T.radarIssue }}
          >
            {triageError}
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {/* Assign input (shown when assignMode active) */}
          {assignMode && (
            <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
              <input
                data-testid="brief-memo-assign-input"
                value={assignInput}
                onChange={(e) => setAssignInput(e.target.value)}
                placeholder="Owner name or email"
                onKeyDown={(e) => e.key === "Enter" && handleAssignConfirm()}
                style={{
                  flex:         1,
                  fontSize:     T.fsXs,
                  padding:      "5px 8px",
                  border:       `1px solid ${T.border}`,
                  borderRadius: T.radiusSm,
                  outline:      "none",
                }}
              />
              <button
                type="button"
                data-testid="brief-memo-assign-confirm"
                onClick={handleAssignConfirm}
                style={{
                  background:   T.blue,
                  color:        T.white,
                  border:       "none",
                  borderRadius: T.radiusSm,
                  padding:      "5px 10px",
                  fontSize:     T.fsXs,
                  fontWeight:   T.fwSemibold,
                  cursor:       "pointer",
                }}
              >
                Set
              </button>
              <button
                type="button"
                onClick={() => { setAssignMode(false); setAssignInput(""); }}
                style={{
                  background:   T.lightGray,
                  color:        T.mid,
                  border:       "none",
                  borderRadius: T.radiusSm,
                  padding:      "5px 8px",
                  fontSize:     T.fsXs,
                  cursor:       "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          )}

          <div
            data-testid="brief-memo-actions"
            style={{ display: "flex", gap: 6, flexWrap: "wrap" }}
          >
            <ActionButton
              label={item.assignedTo ? "Reassign owner" : "Assign owner"}
              onClick={() => { setAssignMode(true); setAssignInput(item.assignedTo ?? ""); }}
              disabled
              primary
            />
            <ActionButton
              label="Create task"
              onClick={() => {}}
              disabled
            />
            <ActionButton
              label="Open record"
              onClick={() => item.navUrl && (onNavigate ? onNavigate(item.navUrl) : (window.location.href = item.navUrl))}
              disabled={!item.navUrl}
            />
            <ActionButton
              label="Mark as reviewed"
              onClick={handleResolve}
              disabled={!isEditor || item.resolved}
            />
          </div>
        </div>
      </div>

      {/* ── AI footer ── */}
      <div style={{
        padding:        "8px 20px",
        borderTop:      `1px solid ${T.border}`,
        flexShrink:     0,
        display:        "flex",
        alignItems:     "center",
        justifyContent: "center",
      }}>
        <span style={{
          fontSize:  T.fsXxs,
          color:     T.light,
          fontStyle: "italic",
        }}>
          ✦ Surfaced by Ripple AI · grounded in live portfolio data
        </span>
      </div>
    </div>
  );
}
